import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import './CalendarPage.css';
import { SideMenuBar } from '../../SideMenu';
import { NewHeader } from '../../NewHeader';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { API_URL } from '../../Config';
import { useAuth } from '../../AuthContext';

const Dashboard = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const { user } = useAuth();
  const username = user.user;
  const calendarRef = useRef(null);

  const fetchEventsFromBackend = useCallback(async () => {
    try {
      let endpoint = API_URL + `/tasks/?assignee=${username.username}`;

      if (username.role === 'Admin' || username.role === 'HR') {
        endpoint = API_URL + '/tasks/all';
      }

      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      return data.map((task) => ({
        title: task.taskDetails,
        start: task.deadline,
        assignee: task.assignee,
        priority: task.priority,
        status: task.status,
        category: task.category,
      }));
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    }
  }, [username]);

  useEffect(() => {
    fetchEventsFromBackend().then((data) => setEvents(data));
  }, [username, fetchEventsFromBackend]);

  const handleEventClick = (arg) => {
    setSelectedEvent(arg.event);
  };

  const closePopup = () => {
    setSelectedEvent(null);
  };

  const handleToday = () => {
    calendarRef.current?.getApi().today();
  };

  const eventStats = useMemo(() => {
    const total = events.length;
    const completed = events.filter(
      (event) => (event.status || '').toLowerCase() === 'completed'
    ).length;
    const pending = events.filter(
      (event) => (event.status || '').toLowerCase() === 'pending'
    ).length;

    return {
      total,
      completed,
      pending,
      scheduled: Math.max(total - completed - pending, 0),
    };
  }, [events]);

  const getStatusClass = (status) => {
    const value = (status || '').toLowerCase().replace(/\s+/g, '-');
    if (value === 'completed') return 'status_completed';
    if (value === 'in-process') return 'status_process';
    return 'status_pending';
  };

  return (
    <div className="calendar_app">
      <SideMenuBar />

      <main className="content">
        <NewHeader />

        <div className="calendar_container">
          <section className="calendar_page_heading">
            <div>
              <span className="page_kicker">WORK MANAGEMENT / SCHEDULE</span>
              <h1>Calendar</h1>
              <p>Plan deadlines, review workload and keep your team aligned.</p>
            </div>

            <button type="button" className="today_header_btn" onClick={handleToday}>
              <span>◷</span>
              Today
            </button>
          </section>

          <section className="calendar_stats">
            <div className="calendar_stat">
              <strong>{eventStats.total}</strong>
              <span>Total tasks</span>
            </div>
            <div className="calendar_stat">
              <strong>{eventStats.pending}</strong>
              <span>Pending</span>
            </div>
            <div className="calendar_stat">
              <strong>{eventStats.scheduled}</strong>
              <span>In process</span>
            </div>
            <div className="calendar_stat">
              <strong>{eventStats.completed}</strong>
              <span>Completed</span>
            </div>
            <div className="calendar_live">
              <span className="calendar_live_dot" />
              <span>Live schedule</span>
            </div>
          </section>

          <section className="calendar_workspace">
            <div className="calendar_workspace_header">
              <div>
                <span className="page_kicker">TASK SCHEDULE</span>
                <h2>Team calendar</h2>
              </div>

              <div className="calendar_workspace_meta">
                <span>{events.length} scheduled records</span>
              </div>
            </div>

            <div className="calendar_canvas">
              <FullCalendar
                ref={calendarRef}
                plugins={[
                  dayGridPlugin,
                  timeGridPlugin,
                  listPlugin,
                  interactionPlugin,
                ]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next',
                  center: 'title',
                  right: 'today dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                }}
                buttonText={{
                  today: 'Today',
                  month: 'Month',
                  week: 'Week',
                  day: 'Day',
                  list: 'Agenda',
                }}
                events={events}
                editable={true}
                selectable={true}
                selectMirror={true}
                dayMaxEvents={(info) =>
                  info.dayEvents.length > 1 ? true : 1
                }
                eventClick={handleEventClick}
                eventClassNames={(arg) => [
                  `calendar_event_${getStatusClass(
                    arg.event.extendedProps.status
                  )}`,
                ]}
                eventContent={(arg) => (
                  <div className="calendar_event_content">
                    <div className="calendar_event_title">
                      <span className="calendar_event_time">
                        {arg.timeText}
                      </span>
                      <strong>{arg.event.title}</strong>
                    </div>

                    <span className="calendar_event_assignee">
                      {arg.event.extendedProps.assignee || 'Unassigned'}
                    </span>
                  </div>
                )}
              />
            </div>
          </section>
        </div>
      </main>

      {selectedEvent && (
        <div className="popup">
          <div className="overlay" onClick={closePopup} />

          <div
            className="popup-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="calendar-event-title"
          >
            <div className="popup-header">
              <div>
                <span className="page_kicker">SCHEDULED TASK</span>
                <h2 id="calendar-event-title">Event details</h2>
              </div>

              <button
                type="button"
                className="popup-close"
                onClick={closePopup}
                aria-label="Close event details"
              >
                ×
              </button>
            </div>

            <div className="event_detail_identity">
              <span className="event_detail_avatar">
                {(selectedEvent.extendedProps.assignee || '?')
                  .charAt(0)
                  .toUpperCase()}
              </span>
              <div>
                <strong>{selectedEvent.title || 'Untitled task'}</strong>
                <span>
                  {selectedEvent.extendedProps.category || 'General task'}
                </span>
              </div>
            </div>

            <div className="event_detail_grid">
              <div>
                <span>Assignee</span>
                <strong>
                  {selectedEvent.extendedProps.assignee || 'Unassigned'}
                </strong>
              </div>

              <div>
                <span>Status</span>
                <b
                  className={`event_status_chip ${getStatusClass(
                    selectedEvent.extendedProps.status
                  )}`}
                >
                  {selectedEvent.extendedProps.status || 'Not available'}
                </b>
              </div>

              <div>
                <span>Priority</span>
                <strong>
                  {selectedEvent.extendedProps.priority || 'Not available'}
                </strong>
              </div>

              <div>
                <span>Deadline</span>
                <strong>
                  {selectedEvent.start
                    ? selectedEvent.start.toLocaleString()
                    : 'Not available'}
                </strong>
              </div>
            </div>

            <button type="button" className="popup-action" onClick={closePopup}>
              Close details
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
